import { ArrowUpRight, Check, Send, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { accountsEnabled, closeSignIn, openSignIn, useAccount } from "../lib/auth.ts";
import { isDesktop, openExternal } from "../lib/desktop.ts";
import { clearFeedbackDraft, closeFeedback, feedbackAccessToken, feedbackFacts, useFeedback } from "../lib/feedback.ts";
import {
  checkDraft, contextLine, DEFAULT_LIMITS, FEEDBACK_URL, feedbackRequestBody, fetchFeedbackForm, FORM_MAX_AGE_MS, FORM_MIN_AGE_MS,
  sendFeedbackPost, solveProofOfWork, waitsForApproval, type FeedbackContext, type FeedbackForm, type Problem,
} from "../lib/feedbackForm.ts";
import { useApp } from "../state/store.ts";
import { AcademyLink } from "./Academy.tsx";
import { LegalLink } from "./Legal.tsx";

type FormState = { s: "loading" } | { s: "ready"; form: FeedbackForm; at: number; used: boolean } | { s: "failed"; problem: Problem };
type SendState =
  | { s: "idle" }
  | { s: "checking" }
  | { s: "sending" }
  | { s: "sent"; visibility: "pending" | "shown"; url: string | null; anonymous: boolean }
  | { s: "error"; problem: Problem };
type Pow = { salt: string; ctrl: AbortController; promise: Promise<string | null> };

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
/** a form this close to running out is swapped for a new one before sending */
const FORM_FRESH_MS = FORM_MAX_AGE_MS - 5 * 60_000;
const problem = (kind: Problem["kind"], message: string): Problem => ({ kind, code: kind, status: 0, message });

/** A link to the feedback board (or a post on it): a new tab in the browser, the system browser in the desktop app. */
function BoardLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a
      className="learn-more"
      href={href}
      target="_blank"
      rel="noopener"
      aria-label={label}
      onClick={(e) => { if (isDesktop()) { e.preventDefault(); void openExternal(href); } }}
    >
      {children}<ArrowUpRight className="trail" size="1em" aria-hidden="true" />
    </a>
  );
}

/** Help (?) › Send feedback: a post on MC Planner's board, sent from here (lib/feedbackForm.ts). */
export function FeedbackDialog() {
  const open = useFeedback((s) => s.open);
  return open ? <FeedbackBody /> : null;
}

function FeedbackBody() {
  const title = useFeedback((s) => s.title);
  const body = useFeedback((s) => s.body);
  const setDraft = useFeedback((s) => s.set);
  const status = useAccount((s) => s.status);
  const [form, setForm] = useState<FormState>({ s: "loading" });
  const [send, setSend] = useState<SendState>({ s: "idle" });
  const [facts, setFacts] = useState<FeedbackContext | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  // after "Your sign-in has run out" or an unconfirmed email: this post goes without an account
  const [withoutAccount, setWithoutAccount] = useState(false);

  const formRef = useRef<FormState>({ s: "loading" });
  const life = useRef<AbortController | null>(null);
  const inflight = useRef<Promise<FeedbackForm | null> | null>(null);
  const pow = useRef<Pow | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const titleInput = useRef<HTMLInputElement>(null);
  const bodyInput = useRef<HTMLTextAreaElement>(null);

  const signedIn = status === "signed-in" && !withoutAccount;
  const busy = send.s === "checking" || send.s === "sending";
  const sent = send.s === "sent";
  const ready = form.s === "ready" ? form : null;
  const limits = ready?.form.limits ?? DEFAULT_LIMITS;
  const anonymousOff = !signedIn && !!ready && !ready.form.anonymous;

  const putForm = (next: FormState) => { formRef.current = next; setForm(next); };

  /** a new form (GET, nothing about anyone): when the dialog opens, and after one was used up or ran out */
  const loadForm = useCallback((quiet = false): Promise<FeedbackForm | null> => {
    if (inflight.current) return inflight.current;
    const signal = life.current?.signal;
    if (!quiet) putForm({ s: "loading" });
    const p = fetchFeedbackForm({ signal }).then((r) => {
      if (inflight.current === p) inflight.current = null;
      if (signal?.aborted) return null;
      putForm(r.ok ? { s: "ready", form: r.form, at: Date.now(), used: false } : { s: "failed", problem: r.problem });
      return r.ok ? r.form : null;
    });
    inflight.current = p;
    return p;
  }, []);

  /** the proof-of-work for this form's salt (one at a time; a new form stops the old one) */
  const solve = useCallback((f: FeedbackForm): Promise<string | null> => {
    const cur = pow.current;
    if (cur?.salt === f.salt) return cur.promise;
    cur?.ctrl.abort();
    const ctrl = new AbortController();
    life.current?.signal.addEventListener("abort", () => ctrl.abort());
    const entry: Pow = { salt: f.salt, ctrl, promise: solveProofOfWork(f.salt, f.bits, { signal: ctrl.signal }) };
    pow.current = entry;
    return entry.promise;
  }, []);

  // open: a new form, what goes with the post, and whether we're online
  useEffect(() => {
    const ctrl = new AbortController();
    life.current = ctrl;
    if (navigator.onLine === false) putForm({ s: "failed", problem: problem("offline", "You're offline.") });
    else void loadForm();
    const s = useApp.getState();
    void feedbackFacts({ demo: s.demo, panel: s.layoutPanel, board: !!s.boardFor }).then((c) => { if (!ctrl.signal.aborted) setFacts(c); });
    const onOnline = () => {
      setOnline(true);
      if (formRef.current.s === "failed") void loadForm();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      ctrl.abort();
      inflight.current = null;
      pow.current = null;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [loadForm]);

  // signing in (again) posts as the account
  useEffect(() => { if (status === "signed-in") setWithoutAccount(false); }, [status]);

  // without an account: solve the proof-of-work as soon as they start typing, so Send is quick
  const typed = title.length > 0 || body.length > 0;
  useEffect(() => {
    if (typed && !signedIn && ready && ready.form.anonymous && !ready.used) void solve(ready.form);
  }, [typed, signedIn, ready, solve]);

  const close = () => { if (!busy) closeFeedback(); };

  const onSend = async () => {
    if (busy || sent || status === "checking") return;
    const draftProblem = checkDraft(title, body, limits);
    if (draftProblem) {
      setSend({ s: "error", problem: problem("error", draftProblem) });
      (checkDraft(title, "", limits) ? titleInput : bodyInput).current?.focus();
      return;
    }
    if (navigator.onLine === false) {
      setSend({ s: "error", problem: problem("offline", "You're offline. Connect to the internet, then press Send again.") });
      return;
    }
    if (!facts) return;
    const anonymous = !signedIn;
    const signal = life.current?.signal;
    setSend({ s: anonymous ? "checking" : "sending" });

    // a form that was used up (without an account, one form buys one post) or is about to run out: a new one first
    let cur = formRef.current;
    if (cur.s !== "ready" || cur.used || Date.now() - cur.at > FORM_FRESH_MS) {
      await loadForm(cur.s === "ready");
      if (signal?.aborted) return;
      cur = formRef.current;
      if (cur.s !== "ready") { setSend({ s: "idle" }); return; } // the form's own problem shows
    }
    const f = cur.form;
    if (anonymous && !f.anonymous) { setSend({ s: "idle" }); return; } // offers sign-in instead

    let nonce: string | null = null;
    if (anonymous) {
      nonce = await solve(f);
      if (signal?.aborted) return;
      if (!nonce) { setSend({ s: "error", problem: problem("error", "The spam check didn't finish. Please press Send again.") }); return; }
    }
    // a form sent back within 3 seconds of being made is refused: wait out the rest
    const early = FORM_MIN_AGE_MS + 300 - (Date.now() - cur.at);
    if (early > 0) await sleep(early);
    if (signal?.aborted) return;
    setSend({ s: "sending" });

    const token = anonymous ? null : await feedbackAccessToken();
    if (signal?.aborted) return;
    if (!anonymous && !token) {
      setSend({ s: "error", problem: problem("unauthorized", "Your sign-in has run out. Sign in again, or send it without an account.") });
      return;
    }
    const res = await sendFeedbackPost(feedbackRequestBody({ title, body, token: f.token, nonce, context: facts }), { accessToken: token });
    if (signal?.aborted) return;

    const now = formRef.current;
    // without an account the form is spent once it reaches the server; an expired one is no use either
    const used = anonymous || (!res.ok && res.problem.kind === "expired");
    if (now.s === "ready" && now.form.token === f.token) {
      const off = !res.ok && res.problem.kind === "anonymous_off";
      if (used || off) putForm({ ...now, used, form: off ? { ...now.form, anonymous: false } : now.form });
    }
    if (res.ok) {
      setSend({ s: "sent", visibility: res.visibility, url: res.url, anonymous });
      clearFeedbackDraft();
      return;
    }
    setSend({ s: "error", problem: res.problem });
    // ready for the next Send: a new form now (a GET; nothing is sent until they press Send again)
    if (used && res.problem.kind !== "offline") void loadForm(true);
  };

  // the dialog keeps the keyboard: Escape closes it, Tab stays inside, and the app's own shortcuts (controller.ts listens
  // on window: Delete, the tools, Ctrl+S, copying or cutting a plan object) never act behind it
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      // the sign-in dialog opened from here sits on top: Escape closes that one first
      if (useAccount.getState().dialog.open) closeSignIn();
      else close();
      return;
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void onSend(); return; }
    if (e.code === "KeyS" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); return; } // not the browser's "save page"
    if (e.key !== "Tab") return;
    const items = [...(box.current?.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href]") ?? [])];
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || !box.current?.contains(at))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (at === last || !box.current?.contains(at))) { e.preventDefault(); first.focus(); }
  };
  const keepClipboard = (e: { stopPropagation: () => void }) => e.stopPropagation();

  const waits = ready ? waitsForApproval(ready.form, signedIn) : true;
  const board = ready?.form.board ?? FEEDBACK_URL;
  const boardLabel = "Open the board (MC Planner's feedback board, opens in your browser)";
  const shownProblem = send.s === "error" ? send.problem : form.s === "failed" && send.s === "idle" ? form.problem : null;
  const statusText = shownProblem?.message
    ?? (!online ? "You're offline. You can write it now and send it once you're back online." : send.s === "checking" ? "Running the spam check…" : "");
  const bodyChars = Array.from(body).length;
  const label = send.s === "checking" ? "Checking…" : send.s === "sending" ? "Sending…" : "Send";

  return (
    <div className="modal-back" onClick={close} onCopy={keepClipboard} onCut={keepClipboard} onPaste={keepClipboard}>
      <div ref={box} className="modal feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="row between">
          <h3 id="feedback-title">Send feedback</h3>
          <button className="ghost" onClick={close} disabled={busy} title="Close (Esc)" aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>

        {send.s === "sent" ? (
          <>
            <div className="fb-sent" role="status">
              <span className="fb-badge" aria-hidden="true"><Check size={20} strokeWidth={2.5} /></span>
              <div>
                <p><b>Sent, thank you.</b></p>
                <p className="small">
                  {send.visibility === "shown"
                    ? "It's on the board now."
                    : send.anonymous
                      ? "It waits for Lab43 to approve it. It was sent without an account, so you can't see it on the board until then."
                      : "It waits for Lab43 to approve it. Until then you'll find it on the board under Waiting for approval."}
                </p>
              </div>
            </div>
            <div className="row fb-actions">
              <button className="primary" autoFocus onClick={closeFeedback}>Close</button>
              {send.url
                ? <BoardLink href={send.url} label="See your post (on MC Planner's feedback board, opens in your browser)">See your post</BoardLink>
                : <BoardLink href={board} label={boardLabel}>Open the board</BoardLink>}
            </div>
          </>
        ) : (
          <>
            <p className="small muted">Ideas, problems or wishes: Lab43 reads every post.</p>
            <label className="fb-field">
              <span>Title</span>
              <input
                ref={titleInput}
                autoFocus
                value={title}
                maxLength={limits.titleMax}
                readOnly={busy}
                placeholder="e.g. Let me rotate a path by 45°"
                onChange={(e) => setDraft({ title: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) { e.preventDefault(); bodyInput.current?.focus(); } }}
              />
            </label>
            <label className="fb-field">
              <span>Details <span className="muted">(optional)</span></span>
              <textarea
                ref={bodyInput}
                value={body}
                rows={5}
                maxLength={limits.bodyMax}
                readOnly={busy}
                placeholder="What happened, or what would help?"
                onChange={(e) => setDraft({ body: e.target.value })}
              />
              {bodyChars > limits.bodyMax * 0.8 && <span className="tiny muted fb-count">{bodyChars.toLocaleString("en-GB")} / {limits.bodyMax.toLocaleString("en-GB")}</span>}
            </label>

            <div className="fb-facts">
              <span className="tiny muted">Sent with it, for Lab43 only:</span>
              <span className="fb-line" aria-busy={!facts}>{facts ? contextLine(facts) : "…"}</span>
              <span className="tiny muted">Nothing about your plans, worlds, schematics or files.</span>
            </div>

            <p className="small">
              {signedIn
                ? "Posted with your Lab43 account, under its display name. "
                : anonymousOff
                  ? null // the status line below says why, and Send becomes Sign in
                  : <>Posted as Anonymous.{accountsEnabled() && status !== "checking" && <> <button className="link" onClick={() => openSignIn("Sign in to post feedback under your name.")}>Sign in</button> to post under your name.</>}{" "}</>}
              {waits ? "Posts are public once Lab43 approves them." : "Posts are public on the board."}
            </p>

            <div className="row fb-actions">
              {anonymousOff ? (
                accountsEnabled() && <button className="primary" onClick={() => openSignIn("Posting feedback without an account is switched off right now.")}>Sign in to send it</button>
              ) : (
                // aria-disabled while busy, so the button keeps the keyboard focus
                <button className="primary" disabled={!facts || status === "checking"} aria-disabled={busy || undefined} onClick={() => void onSend()}>
                  <Send className="lead" size={16} aria-hidden="true" />{label}
                </button>
              )}
              <BoardLink href={board} label={boardLabel}>Open the board</BoardLink>
            </div>
            <p className={`fb-status small${shownProblem ? " err" : ""}`} role="status">
              {anonymousOff
                ? `Sending without an account is switched off right now. ${accountsEnabled() ? "Sign in, or post on the board." : "You can post on the board."}`
                : statusText}
            </p>
            {send.s === "error" && (send.problem.kind === "unauthorized" || send.problem.kind === "email_unconfirmed") && (
              <div className="row fb-next">
                {send.problem.kind === "unauthorized" && accountsEnabled() && (
                  <button onClick={() => openSignIn("Your sign-in has run out. Sign in again to send your feedback.")}>Sign in again</button>
                )}
                {ready?.form.anonymous && (
                  <button onClick={() => { setWithoutAccount(true); setSend({ s: "idle" }); titleInput.current?.focus(); }}>Send it without an account</button>
                )}
              </div>
            )}
          </>
        )}

        <p className="tiny muted fb-privacy">
          Sent to auth.lab43.dev, Lab43's account site.{" "}
          <LegalLink path="legal/privacy.html#feedback">Privacy</LegalLink>
          {" · "}
          <AcademyLink to="feedback" about="sending feedback" />
        </p>
      </div>
    </div>
  );
}
